import { clsx } from 'clsx'

import { Avatar, ChannelAvatar, Flex } from '@buffer-mono/popcorn'

import { graphql, readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import styles from './EngagementAvatar.module.css'

import type { JSX } from 'react'

export const EngagementAvatar_Channel = graphql(/* GraphQL */ `
  fragment EngagementAvatar_Channel on Channel @_unmask {
    service
  }
`)

export type EngagementAvatarProps = {
  /** engagement author's avatar URL */
  avatar: string
  /** engagement author's username, used for letter fallback + color seed */
  username: string
  /** when set, overlays a channel service badge on the author avatar */
  channel?: FragmentOf<typeof EngagementAvatar_Channel>
  /** render connector line below the avatar */
  showConnector?: boolean
  /** whether the engagement has a parent (shows small line above the avatar) */
  hasParent?: boolean
  /** style for the container */
  style?: React.CSSProperties
}

/**
 * Avatar slot for an engagement row (comment or mention). Always shows the
 * engagement author; pass `channel` to add a service badge indicating which
 * inbox the engagement came from (e.g. collapsed cards in the All Channels
 * view).
 */
export const EngagementAvatar = ({
  avatar,
  username,
  channel,
  showConnector = true,
  hasParent = false,
  style,
}: EngagementAvatarProps): JSX.Element => {
  const channelData = readFragment(EngagementAvatar_Channel, channel)
  return (
    <div
      className={clsx(styles.container, hasParent && styles.hasParent)}
      aria-hidden
      style={style}
    >
      <Flex align="center" justify="center" className={styles.avatarContainer}>
        {channelData ? (
          <ChannelAvatar
            size="small"
            channel={channelData.service}
            src={avatar}
            alt={username}
            fallback="letter"
            fallbackName={username}
          />
        ) : (
          <Avatar
            src={avatar}
            alt={username}
            fallback="letter"
            fallbackName={username}
            size="small"
          />
        )}
      </Flex>
      {showConnector && <div className={styles.verticalLine} />}
    </div>
  )
}

EngagementAvatar.displayName = 'EngagementAvatar'
